import React from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {FileText, WarningCircle} from '../../icons';
import {formatCompactMoney} from './panelFormatters';
import './MainCards.scss';

const STATUS_ACTIVE = 'ACTIVE';

// Estados de conexión que NO son "andando". Cada uno tiene su aviso y su verbo, pero los TRES
// llevan a la tab del media plan: la acción real (reconectar la planilla, reanudar la sync) es un
// flujo con permisos de Google, no un botón que se pueda resolver desde el Panel.
const CONNECTION_ISSUES = {
    ERROR: {
        textKey: 'client_panel.media_plan_error',
        textDefault: 'Error al sincronizar la planilla',
        ctaKey: 'client_panel.media_plan_error_cta',
        ctaDefault: 'Reintentar',
    },
    REVOKED: {
        textKey: 'client_panel.media_plan_revoked',
        textDefault: 'El acceso a la planilla expiró',
        ctaKey: 'client_panel.media_plan_revoked_cta',
        ctaDefault: 'Reconectar',
    },
    PAUSED: {
        textKey: 'client_panel.media_plan_paused',
        textDefault: 'Sincronización pausada',
        ctaKey: 'client_panel.media_plan_paused_cta',
        ctaDefault: 'Reanudar',
    },
};

/**
 * Desvío de la proyección del cliente contra el presupuesto DEL PLAN, en porcentaje redondeado.
 *
 * Devuelve `null` si falta cualquiera de los dos números (o el plan es 0): un "0%" inventado diría
 * "el plan se está cumpliendo" cuando en realidad no hay con qué compararlo.
 */
export function planVsProjectionDelta(planBudget, projectionAmount) {
    const budget = Number(planBudget);
    const amount = Number(projectionAmount);
    if (planBudget === null || planBudget === undefined || !Number.isFinite(budget) || budget === 0) return null;
    if (projectionAmount === null || projectionAmount === undefined || !Number.isFinite(amount)) return null;
    return Math.round((amount / budget - 1) * 100);
}

/**
 * La planilla de media plan vinculada, como franja DENTRO del bloque de Líneas de control
 * (`panel.media_plan`).
 *
 * Hasta el 24/09 era una card propia debajo de las líneas. Pablo, en la pasada de pantalla del 23/09
 * (card gW32VovP): «el media plan es para armar líneas de control». Así que vive donde viven las
 * líneas que produce. Sin planilla vinculada no se dibuja nada: la invitación a vincularla es el
 * botón «Sincronizar con planilla de media plan» de la cabecera del bloque (y el del vacío).
 *
 * Con planilla muestra lo mismo que la card: qué planilla es, el plan contra la proyección real, lo
 * que espera una decisión, y si la sincronización se cayó.
 *
 * `projection` y `currency` llegan del shell (`kpis.projection` y `client.currency`): la franja no
 * hace fetch propio ni recalcula nada, sólo compara los dos números del agregado.
 */
export function MediaPlanStrip({data, projection, currency, clientId}) {
    const {t} = useTranslation();

    // `enabled: false` ⇒ el resto de las claves ni vienen (contrato).
    if (!data || data.enabled === false || !data.connected) return null;

    const mediaPlanUrl = `/client/${clientId}/media-plan`;
    const connection = data.connection || {};
    const issue = CONNECTION_ISSUES[connection.status];
    const pending = Number(data.pending_items) || 0;

    const planBudget = formatCompactMoney(currency, data.plan_budget);
    const projected = formatCompactMoney(currency, projection && projection.amount);
    const delta = planVsProjectionDelta(data.plan_budget, projection && projection.amount);
    // Pasarse del plan avisa en oro; quedarse en el plan (o por debajo) es verde. Mismo criterio de
    // color que el resto del panel — acá el veredicto es contra el PLAN, no contra el presupuesto.
    const deltaClass = delta !== null && delta > 0 ? 'pnl-delta--gold' : 'pnl-delta--green';

    // El presupuesto del plan vs. la proyección real sólo se muestra con la conexión sana: con la
    // sync caída los números son de la última vez que anduvo y compararlos sería mentir.
    const renderNumbers = () => {
        if (issue || connection.status !== STATUS_ACTIVE || !planBudget) return null;
        return (
            <>
                <span className="pnl-mp__sep"> · </span>
                {t('client_panel.media_plan_budget_label', 'Presupuesto del plan')}{' '}
                <strong>{planBudget}</strong>
                {projected && (
                    <>
                        <span className="pnl-mp__sep"> · </span>
                        {t('client_panel.media_plan_projection_label', 'proyección real')}{' '}
                        <strong>&asymp; {projected}</strong>
                        {delta !== null && (
                            <>
                                {' ('}
                                <span className={`pnl-delta ${deltaClass}`}>
                                    {delta > 0 ? `+${delta}%` : `${delta}%`}
                                </span>
                                {')'}
                            </>
                        )}
                    </>
                )}
                <span className="pnl-mp__sep"> · </span>
                {t('client_panel.media_plan_autosync', 'se sincroniza sola')}
            </>
        );
    };

    return (
        <div className="pnl-mp-strip">
            {issue && (
                <div className="pnl-banner">
                    <WarningCircle size={18}/>
                    <span className="pnl-banner__txt">{t(issue.textKey, issue.textDefault)}</span>
                    <Link className="pnl-btn pnl-btn--ghost pnl-btn--sm" to={mediaPlanUrl}>
                        {t(issue.ctaKey, issue.ctaDefault)}
                    </Link>
                </div>
            )}

            <div className="pnl-mp-strip__row">
                {/* Ícono y texto juntos: lo que baja de renglón en angosto son la pastilla y el
                    link, nunca el ícono solo arriba del título. */}
                <div className="pnl-mp-strip__main">
                    <FileText size={16} weight="bold"/>
                    <p className="pnl-mp__line pnl-mp-strip__txt">
                        {connection.title
                            ? <strong>{connection.title}</strong>
                            : t('client_panel.media_plan_title', 'Media plan')}
                        {' '}
                        {t('client_panel.media_plan_connected_suffix', '— planilla conectada')}
                        {renderNumbers()}
                    </p>
                </div>
                {/* Lo que espera una decisión: la pastilla ES el camino a revisarlo. Sin pendientes
                    no aparece (un «Revisar» que abre una lista vacía quema al usuario). */}
                {pending > 0 && (
                    <Link className="pnl-pill" to={mediaPlanUrl}>
                        {t('client_panel.media_plan_pending_pill', {
                            count: pending,
                            defaultValue_one: '{{count}} propuesta por revisar',
                            defaultValue_other: '{{count}} propuestas por revisar',
                        })}
                    </Link>
                )}
                <Link className="pnl-card__link" to={mediaPlanUrl}>
                    {t('client_panel.media_plan_open', 'Abrir media plan')} &rarr;
                </Link>
            </div>
        </div>
    );
}
